import { useEffect, useMemo, useState } from "react";
import DepotAddressScan from "./DepotAddressScan";
import DriverAvatar from "../components/DriverPhoto";

/* ------------------------------------------------------------------ *
 * Unit 2A, 1A Hale Street, Botany NSW 2019 — interactive depot floor plan.
 *
 * INTERNAL STAFF TOOL. This shows where each round parks (bay), which wave
 * they're on, and the streets they cover — read from the live driver_routes
 * roster. Staff can drag drivers between bays and set waves to plan the floor.
 *
 * Those drag positions are saved as an INTERNAL OVERLAY (depot-layout-sb,
 * settings KV) layered on top of the route data. This page NEVER writes the
 * route/sort plan (chute/outlet/streets) and NEVER notifies AP. Pushing any
 * of this into the live route data would be a separate, explicitly-authorised
 * action (Paul, 31 Jul 2026 — "no changes in the sort plan unless I authorise
 * it").
 *
 * SCHEMATIC ONLY. Proportions come from the bay allocation spreadsheet and a
 * Google Maps aerial, not a surveyed plan — replace DEPOT_LAYOUT once the
 * Goodman lease plan or NSW LRS strata plan is on hand.
 * ------------------------------------------------------------------ */

const ROUTES_API = "/.netlify/functions/driver-routes-sb";
const LAYOUT_API = "/.netlify/functions/depot-layout-sb";

/** Stable identity for a round in the overlay store: its ADAM code when
 *  present, else 'L:<LOGIN>' for the bay-less rounds whose adam_code is blank. */
const roundKey = (r) => {
  const adam = String(r.adamCode || "").trim();
  if (adam) return adam;
  const login = String(r.login ?? r.driverLogin ?? "").trim().toUpperCase();
  return login ? `L:${login}` : "";
};

/** The driver's PROGRAMMED route identity — the bay + driver number that
 *  travel with the driver regardless of which physical floor slot they park
 *  in. Derived from the ADAM code (e.g. "BAY_318_CITY_D11" → bay 318, D11);
 *  falls back to the seed bay/D-number for rounds with no ADAM code. */
export function programmedInfo(r) {
  const adam = String(r.adamCode || "").trim();
  const parts = adam.split("_"); // ["BAY","318","CITY","D11"]
  const bay = parts[0] === "BAY" && parts[1] ? parts[1] : String(r.bayNumber ?? "").trim();
  const dPart = parts.length >= 4 ? parts[parts.length - 1] : (r.dNumber ? `D${r.dNumber}` : "");
  return { bay, dPart, full: adam || [bay, dPart].filter(Boolean).join(" ") || "—" };
}

export const PALETTE = {
  wall: "#2C2C2A",
  canvas: "#FFFFFF",
  wave: {
    1: { fill: "#E1F5EE", stroke: "#1D9E75", text: "#0F6E56" }, // 1st wave
    2: { fill: "#FAEEDA", stroke: "#BA7517", text: "#854F0B" }, // 2nd wave
    unset: { fill: "#F1EFE8", stroke: "#B4B2A9", text: "#444441" }, // wave not recorded
  },
  empty: { fill: "#FBFBF9", stroke: "#D3D1C7", text: "#9A988F" },
  zone: {
    built: { fill: "#FAECE7", stroke: "#D85A30", text: "#712B13" },
    admin: { fill: "#EEEDFE", stroke: "#534AB7", text: "#3C3489" },
    racking: { fill: "#E6F1FB", stroke: "#378ADD", text: "#0C447C" },
    traffic: { fill: "#F1EFE8", stroke: "#B4B2A9", text: "#444441" },
  },
  accent: "#1D9E75",
  ink: "#2C2C2A",
  inkMuted: "#5F5E5A",
  hairline: "#D3D1C7",
};

const WAVE_LABEL = { 1: "1st wave", 2: "2nd wave", unset: "Wave not set" };
const waveKey = (w) => (w === "1" || w === 1 ? 1 : w === "2" || w === 2 ? 2 : "unset");
const waveColor = (w) => PALETTE.wave[waveKey(w)];

export const DEPOT_LAYOUT = {
  viewBox: "0 0 680 660",
  vbW: 680,
  vbH: 660,
  building: "70,50 640,50 640,564 70,564",
  walls: [
    { id: "diagonal", x1: 260, y1: 300, x2: 355, y2: 50 },
    { id: "office-south", x1: 70, y1: 295, x2: 262, y2: 295 },
  ],
  zones: [
    { id: "amenities", label: "Amenities", kind: "built", points: "70,50 355,50 317,150 70,150", labelAt: [175, 100] },
    { id: "office", label: "Office", kind: "built", points: "70,155 315,155 262,295 70,295", labelAt: [175, 222] },
    { id: "admin", label: "Admin", kind: "admin", points: "70,300 260,300 253,318 70,318", labelAt: [150, 309], small: true },
    { id: "racking", label: "", kind: "racking", rect: { x: 90, y: 327, width: 30, height: 213 } },
  ],
  traffic: {
    points: "254,564 254,327 285,235 520,235 520,564 454,564 454,269 320,269 320,564",
    arrows: [
      [287, 556, 287, 520], [287, 480, 287, 440], [287, 400, 287, 360],
      [290, 336, 299, 296], [312, 252, 368, 252], [404, 252, 460, 252],
      [487, 292, 487, 332], [487, 372, 487, 412], [487, 452, 487, 492], [487, 520, 487, 556],
    ],
    gates: [
      { label: "Entry", x: 287, y: 580 },
      { label: "Exit", x: 487, y: 580 },
    ],
  },
  noUse: { points: "285,235 355,50 520,50 520,235", label: "No-use area", labelAt: [430, 140] },
  banks: [
    { id: "west", x: 124, width: 126, first: 328, count: 8 },
    { id: "centre", x: 324, width: 126, first: 318, count: 10 },
    // East bank slightly compressed (pitch 27) so a spare slot fits above 317.
    { id: "east", x: 524, width: 112, first: 301, count: 17, pitch: 27 },
  ],
  bay: { frontageY: 540, height: 26, pitch: 28 },
  // Named, non-numbered positions with explicit coordinates. "Spare" is the
  // empty floor space above bay 317 (top of the east bank) — a spare spot a
  // driver can be dragged onto like any bay.
  extras: [
    { id: "SPARE", label: "Spare", x: 524, y: 52, width: 112, height: 24 },
  ],
};

/** Expand bank specs into positioned bay rectangles. */
export function generateBays(layout = DEPOT_LAYOUT) {
  const { frontageY, height: defH, pitch: defP } = layout.bay;
  const banks = layout.banks.flatMap((bank) => {
    const h = bank.height ?? defH;
    const p = bank.pitch ?? defP;
    return Array.from({ length: bank.count }, (_, i) => ({
      id: String(bank.first + i),
      bank: bank.id,
      x: bank.x,
      y: frontageY - h - i * p,
      width: bank.width,
      height: h,
    }));
  });
  const extras = (layout.extras || []).map((e) => ({
    id: e.id,
    bank: "extra",
    x: e.x,
    y: e.y,
    width: e.width,
    height: e.height,
    label: e.label || e.id,
    unnumbered: true,
  }));
  return [...banks, ...extras];
}

/** Group (effective) route rows into { bayId: [occupant…] } plus rows that
 *  can't sit on the plan. `idx` is the row's index in the array — the safe
 *  identity for mutation and for computing its overlay key. */
export function buildOccupancy(routes, planBayIds) {
  const onPlan = {};
  const noBay = [];
  const offPlan = [];
  const bayCountByLogin = {};

  routes.forEach((r, idx) => {
    const prog = programmedInfo(r);
    const occ = {
      idx,
      adamCode: r.adamCode || "",
      login: r.driverLogin || "",
      wave: String(r.wave || "").trim(),
      area: r.area || "",
      chute: String(r.chute || ""),
      dNumber: String(r.dNumber || ""),
      outlet: String(r.outlet || ""),
      streets: Array.isArray(r.streets) ? r.streets : [],
      // `bay` = the physical floor slot the driver is PARKED in (placement).
      bay: String(r.physicalBay ?? r.bayNumber ?? "").trim(),
      // Programmed identity — travels with the driver, independent of the slot.
      programmedBay: prog.bay,
      programmedDriver: prog.dPart,
      programmedFull: prog.full,
      moved: !!r.moved,
    };
    if (!occ.bay) {
      noBay.push(occ);
      return;
    }
    if (occ.login) bayCountByLogin[occ.login] = (bayCountByLogin[occ.login] || 0) + 1;
    if (planBayIds.has(occ.bay)) (onPlan[occ.bay] = onPlan[occ.bay] || []).push(occ);
    else offPlan.push(occ);
  });

  const waveRank = (w) => (waveKey(w) === 1 ? 0 : waveKey(w) === 2 ? 1 : 2);
  Object.values(onPlan).forEach((list) => {
    list.sort((a, b) => waveRank(a.wave) - waveRank(b.wave));
    list.forEach((o) => {
      if (bayCountByLogin[o.login] > 1) o.multiBay = bayCountByLogin[o.login];
    });
  });
  return { onPlan, noBay, offPlan };
}

/* ------------------------------------------------------------------ */

export default function DepotMapPage({ title = "Unit 2A — Botany" }) {
  const [routes, setRoutes] = useState(null); // seed from driver_routes
  const [overrides, setOverrides] = useState({}); // internal overlay { key: {bay,wave} }
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(0);
  const [selected, setSelected] = useState(null);
  const [waveFilter, setWaveFilter] = useState("all"); // all | 1 | 2 | unset
  const [areaFilter, setAreaFilter] = useState("all");
  const [drag, setDrag] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // bay id | 'tray'
  const [pendingDrop, setPendingDrop] = useState(null); // { idx, bayId, x, y } — Share/Swap choice
  const [photos, setPhotos] = useState({}); // login -> dataUrl ('' = none, undefined = loading)

  const bays = useMemo(() => generateBays(), []);
  const planBayIds = useMemo(() => new Set(bays.map((b) => b.id)), [bays]);

  const load = () => {
    setError(null);
    setRoutes(null);
    const routesP = fetch(ROUTES_API).then((r) => {
      if (!r.ok) throw new Error(`routes HTTP ${r.status}`);
      return r.json();
    });
    // Layout overlay is best-effort — a plan with no saved positions is still useful.
    const layoutP = fetch(LAYOUT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getLayout" }),
    })
      .then((r) => (r.ok ? r.json() : { overrides: {} }))
      .catch(() => ({ overrides: {} }));
    Promise.all([routesP, layoutP])
      .then(([rd, ld]) => {
        if (!rd || rd.success === false) throw new Error(rd?.error || "Load failed");
        setRoutes(Array.isArray(rd.routes) ? rd.routes : []);
        setOverrides((ld && ld.overrides) || {});
      })
      .catch((e) => setError(e.message || "Load failed"));
  };
  useEffect(load, []);

  // Effective rows. The overlay sets only the PHYSICAL floor slot (+ wave) —
  // it never touches `bayNumber`/`adamCode`, so the driver's PROGRAMMED bay
  // stays with them. `physicalBay` is where they're parked right now.
  const effRoutes = useMemo(() => {
    return (routes || []).map((r) => {
      const programmedBay = String(r.bayNumber || "").trim();
      const ov = overrides[roundKey(r)];
      const physicalBay = ov && "bay" in ov ? String(ov.bay) : programmedBay;
      const wave = ov && "wave" in ov ? ov.wave : r.wave;
      return { ...r, physicalBay, wave, moved: physicalBay !== programmedBay };
    });
  }, [routes, overrides]);

  const { onPlan, noBay, offPlan } = useMemo(
    () => buildOccupancy(effRoutes, planBayIds),
    [effRoutes, planBayIds]
  );

  // Rows for the address scanner — street list + physical floor bay + programmed id.
  const scanRoutes = useMemo(
    () =>
      effRoutes.map((r) => {
        const p = programmedInfo(r);
        return {
          login: r.driverLogin || "",
          streets: Array.isArray(r.streets) ? r.streets : [],
          physicalBay: String(r.physicalBay || "").trim(),
          programmedBay: p.bay,
          programmedFull: p.full,
          wave: String(r.wave || "").trim(),
          area: r.area || "",
        };
      }),
    [effRoutes]
  );

  const areas = useMemo(() => {
    const order = { DARL: 0, CITY: 1, BWAY: 2 };
    const set = new Set((routes || []).map((r) => (r.area || "").trim()).filter(Boolean));
    return [...set].sort((a, b) => (order[a] ?? 9) - (order[b] ?? 9) || a.localeCompare(b));
  }, [routes]);

  const matchOcc = (o) =>
    (waveFilter === "all" || String(waveKey(o.wave)) === waveFilter) &&
    (areaFilter === "all" || o.area === areaFilter);
  const filtering = waveFilter !== "all" || areaFilter !== "all";

  const tally = useMemo(() => {
    const t = { w1: 0, w2: 0, unset: 0, emptyBays: 0 };
    bays.forEach((b) => {
      if ((onPlan[b.id] || []).length === 0) t.emptyBays += 1;
    });
    Object.values(onPlan).forEach((list) =>
      list.forEach((o) => {
        const k = waveKey(o.wave);
        if (k === 1) t.w1 += 1;
        else if (k === 2) t.w2 += 1;
        else t.unset += 1;
      })
    );
    return t;
  }, [bays, onPlan]);

  // ── persistence — INTERNAL overlay only ──────────────────────────
  const persist = async (changes) => {
    const prev = overrides;
    const next = { ...overrides };
    changes.forEach((c) => {
      if (!c.key) return;
      if (c.reset) delete next[c.key];
      else next[c.key] = { bay: String(c.bay ?? ""), wave: String(c.wave ?? "") };
    });
    setOverrides(next); // optimistic
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(LAYOUT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "patchLayout", changes }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      if (j.overrides) setOverrides(j.overrides); // authoritative
      setSavedAt(Date.now());
    } catch (e) {
      setOverrides(prev); // revert
      setSaveError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Move = change the PHYSICAL floor slot only. Programmed bay/adam untouched.
  const moveToBay = (idx, bayId) => {
    const seed = routes[idx];
    const eff = effRoutes[idx];
    if (!seed || String(eff.physicalBay || "").trim() === bayId) return;
    persist([{ key: roundKey(seed), bay: bayId, wave: String(eff.wave || "") }]);
  };

  const clearBay = (idx) => {
    const seed = routes[idx];
    const eff = effRoutes[idx];
    if (!seed || !String(eff.physicalBay || "").trim()) return;
    persist([{ key: roundKey(seed), bay: "", wave: String(eff.wave || "") }]);
  };

  // Swap trades PHYSICAL floor slots only — each driver keeps their own wave
  // and their programmed bay/sort plan (chute/outlet/streets) is never involved.
  const swapPositions = (idxA, idxB) => {
    if (idxA === idxB) return;
    const sA = routes[idxA], sB = routes[idxB];
    const eA = effRoutes[idxA], eB = effRoutes[idxB];
    if (!sA || !sB) return;
    persist([
      { key: roundKey(sA), bay: String(eB.physicalBay || ""), wave: String(eA.wave || "") },
      { key: roundKey(sB), bay: String(eA.physicalBay || ""), wave: String(eB.wave || "") },
    ]);
  };

  const setWave = (idx, wave) => {
    const seed = routes[idx];
    const eff = effRoutes[idx];
    if (!seed || String(eff.wave || "") === String(wave)) return;
    persist([{ key: roundKey(seed), bay: String(eff.physicalBay || ""), wave: String(wave) }]);
  };

  const resetRound = (idx) => {
    const seed = routes[idx];
    if (!seed) return;
    persist([{ key: roundKey(seed), reset: true }]);
  };

  const hasOverride = (idx) => {
    const seed = routes[idx];
    return !!(seed && overrides[roundKey(seed)]);
  };

  // ── driver profile photos (lazy per bay) ─────────────────────────
  const fetchPhoto = (login) => {
    if (!login || login in photos) return;
    setPhotos((p) => ({ ...p, [login]: undefined })); // loading
    fetch("/.netlify/functions/driver-photos-sb", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getPhoto", login }),
    })
      .then((r) => r.json())
      .then((j) => setPhotos((p) => ({ ...p, [login]: (j && j.dataUrl) || "" })))
      .catch(() => setPhotos((p) => ({ ...p, [login]: "" })));
  };

  // Fetch photos for whoever's in the selected bay.
  useEffect(() => {
    if (!selected) return;
    (onPlan[selected] || []).forEach((o) => fetchPhoto(o.login));
    // eslint-disable-next-line
  }, [selected, onPlan]);

  // Batch-load face thumbnails for everyone on the plan (for the bay tiles).
  useEffect(() => {
    if (!routes) return;
    const logins = [...new Set(routes.map((r) => r.driverLogin).filter(Boolean))];
    if (!logins.length) return;
    fetch("/.netlify/functions/driver-photos-sb", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getPhotos", logins }),
    })
      .then((r) => r.json())
      .then((j) => { if (j && j.photos) setPhotos((prev) => ({ ...j.photos, ...prev })); })
      .catch(() => {});
  }, [routes]);

  // Zoom the plan toward the selected bay (translate its centre to the middle,
  // then scale). transform-origin 0 0 so the % translate maths is exact.
  const zoomStyle = useMemo(() => {
    if (!selected) return { transform: "none", transformOrigin: "0 0" };
    const b = bays.find((x) => x.id === selected);
    if (!b) return { transform: "none", transformOrigin: "0 0" };
    const cx = (b.x + b.width / 2) / DEPOT_LAYOUT.vbW;
    const cy = (b.y + b.height / 2) / DEPOT_LAYOUT.vbH;
    const Z = 2.4;
    return {
      transform: `translate(${(0.5 - Z * cx) * 100}%, ${(0.5 - Z * cy) * 100}%) scale(${Z})`,
      transformOrigin: "0 0",
    };
  }, [selected, bays]);

  // ── drag helpers ─────────────────────────────────────────────────
  const onDragStart = (e, occ, from) => {
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(occ.idx)); } catch {}
    setDrag({ idx: occ.idx, login: occ.login, from });
  };
  const endDrag = () => { setDrag(null); setDropTarget(null); };
  const dropIdx = (e) => {
    const raw = drag?.idx ?? Number(e.dataTransfer.getData("text/plain"));
    return Number.isInteger(raw) ? raw : null;
  };
  // Dropping onto a bay: empty → just move; occupied → ask Share vs Swap.
  const handleBayDrop = (e, bayId) => {
    e.preventDefault();
    const idx = dropIdx(e);
    const occ = onPlan[bayId] || [];
    endDrag();
    if (idx == null) return;
    if (occ.some((o) => o.idx === idx)) return; // dropped back on its own bay
    if (occ.length === 0) {
      moveToBay(idx, bayId);
      return;
    }
    setPendingDrop({ idx, bayId, x: e.clientX, y: e.clientY });
  };

  const doShare = () => {
    if (pendingDrop) moveToBay(pendingDrop.idx, pendingDrop.bayId);
    setPendingDrop(null);
  };
  const doSwap = (targetIdx) => {
    if (pendingDrop) swapPositions(pendingDrop.idx, targetIdx);
    setPendingDrop(null);
  };

  const selectedOcc = selected ? onPlan[selected] || [] : [];
  const loading = routes === null && !error;

  return (
    <div style={S.page}>
      <style>{DRAG_CSS}</style>
      <header style={S.header}>
        <div>
          <h1 style={S.title}>{title} — Depot Floor Plan</h1>
          <p style={S.subtitle}>
            Internal staff layout · drag a driver to move · drop onto a driver to swap parking
          </p>
        </div>
        <div style={S.headRight}>
          <div style={S.tally}>
            <Stat n={tally.w1} label="1st wave" color={PALETTE.wave[1].text} />
            <Stat n={tally.w2} label="2nd wave" color={PALETTE.wave[2].text} />
            <Stat n={tally.unset} label="Wave unset" color={PALETTE.wave.unset.text} />
            <Stat n={tally.emptyBays} label="Empty bays" color={PALETTE.empty.text} />
          </div>
          <div style={S.saveBox}>
            {saving ? (
              <span style={{ ...S.saveMsg, color: PALETTE.inkMuted }}>Saving…</span>
            ) : saveError ? (
              <span style={{ ...S.saveMsg, color: "#A32D2D" }}>⚠ {saveError}</span>
            ) : savedAt ? (
              <span style={{ ...S.saveMsg, color: PALETTE.accent }}>✓ Saved</span>
            ) : null}
            <button style={S.refresh} onClick={load} disabled={loading || saving}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
      </header>

      <div style={S.internalBanner}>
        Internal layout only. Each bay number is a fixed floor slot; a driver keeps their programmed bay (e.g. QINS · BAY_318_CITY_D11) even when parked elsewhere. Moving a driver never edits the route/sort plan (chute, outlet, streets) and never notifies AP.
      </div>

      {/* Filters */}
      <div style={S.filters}>
        <FilterGroup
          label="Wave"
          value={waveFilter}
          onChange={setWaveFilter}
          options={[
            { v: "all", label: "All" },
            { v: "1", label: "1st", color: PALETTE.wave[1] },
            { v: "2", label: "2nd", color: PALETTE.wave[2] },
            { v: "unset", label: "Unset", color: PALETTE.wave.unset },
          ]}
        />
        <FilterGroup
          label="Area"
          value={areaFilter}
          onChange={setAreaFilter}
          options={[{ v: "all", label: "All" }, ...areas.map((a) => ({ v: a, label: a }))]}
        />
        {filtering && (
          <button style={S.clearFilter} onClick={() => { setWaveFilter("all"); setAreaFilter("all"); }}>
            Clear filters
          </button>
        )}
        <div style={{ marginLeft: "auto" }}>
          <DepotAddressScan routes={scanRoutes} />
        </div>
      </div>

      {error && (
        <div style={S.error}>
          Couldn’t load driver routes: {error}.{" "}
          <button style={S.linkBtn} onClick={load}>Try again</button>
        </div>
      )}

      <div style={S.body}>
        <div style={S.mapCol}>
          {loading ? (
            <div style={S.loading}>Loading floor plan…</div>
          ) : (
            <div style={S.stageViewport}>
             <div style={{ ...S.stage, ...zoomStyle, transition: "transform 0.45s cubic-bezier(0.22,0.61,0.36,1)" }}>
              <svg viewBox={DEPOT_LAYOUT.viewBox} style={S.svg} aria-hidden="true">
                <defs>
                  <marker id="hp-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M2 1L8 5L2 9" fill="none" stroke={PALETTE.inkMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </marker>
                  <pattern id="hp-hatch" width="9" height="9" patternUnits="userSpaceOnUse">
                    <path d="M0 9L9 0" stroke={PALETTE.hairline} strokeWidth="0.5" fill="none" />
                  </pattern>
                </defs>

                <polygon points={DEPOT_LAYOUT.traffic.points} fill={PALETTE.zone.traffic.fill} stroke={PALETTE.zone.traffic.stroke} strokeWidth="0.5" />
                <polygon points={DEPOT_LAYOUT.noUse.points} fill="url(#hp-hatch)" stroke={PALETTE.hairline} strokeWidth="0.5" strokeDasharray="4 3" />
                <text x={DEPOT_LAYOUT.noUse.labelAt[0]} y={DEPOT_LAYOUT.noUse.labelAt[1]} style={S.zoneLabelSm} fill={PALETTE.inkMuted}>
                  {DEPOT_LAYOUT.noUse.label}
                </text>

                {DEPOT_LAYOUT.zones.map((zone) => {
                  const c = PALETTE.zone[zone.kind];
                  return (
                    <g key={zone.id}>
                      {zone.points ? (
                        <polygon points={zone.points} fill={c.fill} stroke={c.stroke} strokeWidth="0.5" />
                      ) : (
                        <rect {...zone.rect} rx="3" fill={c.fill} stroke={c.stroke} strokeWidth="0.5" />
                      )}
                      {zone.label && (
                        <text x={zone.labelAt[0]} y={zone.labelAt[1]} style={zone.small ? S.zoneLabelSm : S.zoneLabel} fill={c.text}>
                          {zone.label}
                        </text>
                      )}
                    </g>
                  );
                })}

                {DEPOT_LAYOUT.walls.map((w) => (
                  <line key={w.id} x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} stroke={PALETTE.wall} strokeWidth="3" strokeLinecap="round" />
                ))}
                <polygon points={DEPOT_LAYOUT.building} fill="none" stroke={PALETTE.wall} strokeWidth="2.5" strokeLinejoin="round" />
                {DEPOT_LAYOUT.traffic.arrows.map(([x1, y1, x2, y2], i) => (
                  <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={PALETTE.inkMuted} strokeWidth="1.5" strokeLinecap="round" markerEnd="url(#hp-arrow)" />
                ))}
                {DEPOT_LAYOUT.traffic.gates.map((g) => (
                  <text key={g.label} x={g.x} y={g.y} style={S.gateLabel} fill={PALETTE.ink}>{g.label}</text>
                ))}
              </svg>

              {/* HTML bay tiles overlaid on the SVG for native drag & drop */}
              <div style={S.overlay}>
                {bays.map((bay) => (
                  <BayTile
                    key={bay.id}
                    bay={bay}
                    occupants={onPlan[bay.id] || []}
                    selected={selected === bay.id}
                    isDropTarget={dropTarget === bay.id}
                    filtering={filtering}
                    matchOcc={matchOcc}
                    photos={photos}
                    onSelect={() => setSelected((cur) => (cur === bay.id ? null : bay.id))}
                    onDragStart={onDragStart}
                    onDragEnd={endDrag}
                    onBayDrop={handleBayDrop}
                    onDragOverBay={(e) => { e.preventDefault(); if (dropTarget !== bay.id) setDropTarget(bay.id); }}
                  />
                ))}
              </div>
             </div>
             {selected && (
               <button style={S.zoomOut} onClick={() => setSelected(null)} title="Show the whole floor plan">
                 ⤢ Whole plan
               </button>
             )}
            </div>
          )}
          {!loading && (
            <p style={S.hint}>
              Schematic, not to scale. Positions are an internal overlay — the live route data is untouched.
            </p>
          )}
        </div>

        <aside style={S.panel}>
          {selected ? (
            <div style={S.detail}>
              <div style={S.panelKicker}>Bay</div>
              <div style={S.panelBay}>{selected}</div>
              {selectedOcc.length === 0 ? (
                <p style={S.empty}>Empty bay — drag a driver here to park them.</p>
              ) : (
                selectedOcc.map((o) => (
                  <OccupantCard
                    key={o.idx}
                    occ={o}
                    photo={photos[o.login]}
                    onPhotoChanged={(login, url) => setPhotos((p) => ({ ...p, [login]: url }))}
                    onSetWave={setWave}
                    onClearBay={clearBay}
                    onReset={resetRound}
                    moved={hasOverride(o.idx)}
                    busy={saving}
                  />
                ))
              )}
              <button style={S.clear} onClick={() => setSelected(null)}>Clear selection</button>
            </div>
          ) : (
            <div style={S.detail}>
              <div style={S.panelKicker}>No bay selected</div>
              <p style={S.empty}>Select a bay to see its driver login, wave and usual streets — or drag any driver to a new bay.</p>
            </div>
          )}

          {/* Unassigned tray — below the driver/street detail */}
          <div
            style={{ ...S.tray, ...(dropTarget === "tray" ? S.trayActive : null) }}
            onDragOver={(e) => { e.preventDefault(); if (dropTarget !== "tray") setDropTarget("tray"); }}
            onDragLeave={() => setDropTarget((t) => (t === "tray" ? null : t))}
            onDrop={(e) => { e.preventDefault(); const idx = dropIdx(e); endDrag(); if (idx != null) clearBay(idx); }}
          >
            <div style={S.legendHeading}>Unassigned drivers ({noBay.length})</div>
            {noBay.length === 0 ? (
              <p style={S.emptySmall}>Every driver has a bay. Drop a driver here to unassign.</p>
            ) : (
              <div style={S.chips}>
                {noBay.map((o) => (
                  <span
                    key={o.idx}
                    className="hp-draggable"
                    draggable
                    onDragStart={(e) => onDragStart(e, o, "tray")}
                    onDragEnd={endDrag}
                    title={`${o.login}${o.area ? " · " + o.area : ""}`}
                    style={{ ...S.chip, opacity: filtering && !matchOcc(o) ? 0.3 : 1 }}
                  >
                    {o.login || "(blank)"}
                  </span>
                ))}
              </div>
            )}
          </div>

          {offPlan.length > 0 && (
            <div style={S.attention}>
              <div style={S.legendHeading}>Off-plan bay numbers</div>
              {offPlan.map((o) => (
                <div key={o.idx} style={S.attnRow}>
                  <span style={S.attnBay}>Bay {o.bay}</span>
                  <span>{o.login || "—"}</span>
                </div>
              ))}
            </div>
          )}

          <div style={S.legend}>
            <div style={S.legendHeading}>Legend</div>
            <Swatch fill={PALETTE.wave[1].fill} stroke={PALETTE.wave[1].stroke} label="1st wave" />
            <Swatch fill={PALETTE.wave[2].fill} stroke={PALETTE.wave[2].stroke} label="2nd wave" />
            <Swatch fill={PALETTE.wave.unset.fill} stroke={PALETTE.wave.unset.stroke} label="Wave not set" />
            <Swatch fill={PALETTE.empty.fill} stroke={PALETTE.empty.stroke} label="Empty bay" />
            <Swatch fill={PALETTE.zone.built.fill} stroke={PALETTE.zone.built.stroke} label="Office, amenities" />
            <Swatch fill="transparent" stroke={PALETTE.hairline} label="No-use area" />
          </div>
        </aside>
      </div>

      {pendingDrop && (
        <DropMenu
          pending={pendingDrop}
          draggedLogin={routes[pendingDrop.idx]?.driverLogin || "driver"}
          occupants={onPlan[pendingDrop.bayId] || []}
          onShare={doShare}
          onSwap={doSwap}
          onCancel={() => setPendingDrop(null)}
        />
      )}
    </div>
  );
}

/** One HTML bay tile positioned by percentage over the SVG. */
function BayTile({
  bay, occupants, selected, isDropTarget, filtering, matchOcc, photos,
  onSelect, onDragStart, onDragEnd, onBayDrop, onDragOverBay,
}) {
  const { x, y, width, height, id, label, unnumbered } = bay;
  const pos = {
    left: `${(x / DEPOT_LAYOUT.vbW) * 100}%`,
    top: `${(y / DEPOT_LAYOUT.vbH) * 100}%`,
    width: `${(width / DEPOT_LAYOUT.vbW) * 100}%`,
    height: `${(height / DEPOT_LAYOUT.vbH) * 100}%`,
  };
  const n = occupants.length;
  const empty = n === 0;

  return (
    <div
      style={{
        ...S.bay,
        ...pos,
        borderColor: isDropTarget ? PALETTE.accent : selected ? PALETTE.ink : (empty ? PALETTE.empty.stroke : "transparent"),
        borderWidth: isDropTarget || selected ? 2 : 1,
        borderStyle: unnumbered && empty && !selected && !isDropTarget ? "dashed" : "solid",
        background: empty ? PALETTE.empty.fill : "transparent",
        boxShadow: isDropTarget ? `0 0 0 3px rgba(29,158,117,0.25)` : "none",
      }}
      onClick={onSelect}
      onDragOver={onDragOverBay}
      onDrop={(e) => onBayDrop(e, id)}
      role="button"
      tabIndex={0}
      aria-label={empty ? `Bay ${label || id}, empty` : `Bay ${label || id}: ${occupants.map((o) => o.login).join(", ")}`}
    >
      <span style={S.bayNum}>{label || id}</span>
      {!empty && (
        <div style={S.bayRow}>
          {occupants.map((o) => {
            const c = waveColor(o.wave);
            const dim = filtering && !matchOcc(o);
            const photo = photos && photos[o.login];
            return (
              <span
                key={o.idx}
                className="hp-draggable"
                draggable
                onDragStart={(e) => { e.stopPropagation(); onDragStart(e, o, id); }}
                onDragEnd={onDragEnd}
                onClick={(e) => { e.stopPropagation(); onSelect(); }}
                title={`${o.login} — programmed ${o.programmedFull} · ${WAVE_LABEL[waveKey(o.wave)]}${o.moved ? ` · parked in bay ${o.bay} (programmed bay ${o.programmedBay})` : ""} — drop a driver on this bay to share or swap`}
                style={{
                  ...S.chip2,
                  flex: 1,
                  background: c.fill,
                  color: c.text,
                  borderColor: c.stroke,
                  opacity: dim ? 0.28 : 1,
                }}
              >
                {selected && photo ? (
                  <img src={photo} alt={o.login} style={S.chipAvatar} />
                ) : (
                  <>
                    <span style={{ ...S.chipLogin, ...(o.moved ? { textDecoration: "underline" } : null) }}>{o.login}</span>
                    <span style={{ ...S.chipProg, ...(o.moved ? S.chipProgMoved : null) }}>
                      {o.programmedBay}{o.programmedDriver ? `·${o.programmedDriver}` : ""}
                    </span>
                  </>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Share-vs-swap choice shown when a driver is dropped onto an occupied bay. */
function DropMenu({ pending, draggedLogin, occupants, onShare, onSwap, onCancel }) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const left = Math.min(pending.x, vw - 240);
  const top = Math.min(pending.y, vh - (120 + occupants.length * 40));
  return (
    <>
      <div style={S.menuBackdrop} onClick={onCancel} />
      <div style={{ ...S.menu, left, top }} role="menu">
        <div style={S.menuTitle}>
          Bay {pending.bayId} — {draggedLogin}
        </div>
        <button style={{ ...S.menuBtn, ...S.menuShare }} onClick={onShare}>
          <span style={S.menuBtnMain}>Share bay</span>
          <span style={S.menuBtnSub}>keep both — add {draggedLogin} here</span>
        </button>
        {occupants.map((o) => (
          <button key={o.idx} style={S.menuBtn} onClick={() => onSwap(o.idx)}>
            <span style={S.menuBtnMain}>Swap with {o.login}</span>
            <span style={S.menuBtnSub}>they take {draggedLogin}’s spot</span>
          </button>
        ))}
        <button style={S.menuCancel} onClick={onCancel}>Cancel</button>
      </div>
    </>
  );
}

function OccupantCard({ occ, photo, onPhotoChanged, onSetWave, onClearBay, onReset, moved, busy }) {
  const c = waveColor(occ.wave);
  return (
    <div style={S.card}>
      <div style={S.profileHead}>
        <DriverAvatar login={occ.login} photo={photo} size={64} editable onUploaded={(url) => onPhotoChanged(occ.login, url)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.profileTop}>
            <span style={S.cardLogin}>{occ.login || "—"}</span>
            <span style={{ ...S.waveBadge, color: c.text, background: c.fill, borderColor: c.stroke }}>
              {WAVE_LABEL[waveKey(occ.wave)]}
            </span>
          </div>
          <div style={S.bayTag} title="Programmed bay tag">{occ.programmedFull}</div>
        </div>
      </div>
      <div style={S.progBlock}>
        <div style={S.progRow}>
          <span style={S.progLabel}>Programmed</span>
          <span style={S.progVal}>{occ.programmedFull}</span>
        </div>
        <div style={S.progRow}>
          <span style={S.progLabel}>Parked in</span>
          <span style={S.progVal}>
            Bay {occ.bay}
            {moved
              ? <span style={S.progMovedNote}> · moved from bay {occ.programmedBay}</span>
              : <span style={S.progAsProg}> · as programmed</span>}
          </span>
        </div>
      </div>
      <div style={S.cardMeta}>
        {occ.area && <span style={S.metaTag}>{occ.area}</span>}
        {occ.dNumber && <span style={S.metaTag}>D{occ.dNumber}</span>}
        {occ.chute && <span style={S.metaTag}>Chute {occ.chute}</span>}
        {occ.outlet && <span style={S.metaTag}>Outlet {occ.outlet}</span>}
        {moved && <span style={{ ...S.metaTag, color: PALETTE.accent, borderColor: PALETTE.accent }}>moved</span>}
      </div>
      {occ.multiBay && (
        <div style={S.multiNote}>ℹ On {occ.multiBay} bays — this driver runs multiple rounds.</div>
      )}
      <div style={S.waveRow}>
        <span style={S.waveRowLabel}>Wave</span>
        {[
          { v: "1", label: "1st" },
          { v: "2", label: "2nd" },
          { v: "", label: "None" },
        ].map((opt) => {
          const active = String(occ.wave || "") === opt.v;
          return (
            <button key={opt.label} disabled={busy} onClick={() => onSetWave(occ.idx, opt.v)} style={{ ...S.waveBtn, ...(active ? S.waveBtnActive : null) }}>
              {opt.label}
            </button>
          );
        })}
        <button disabled={busy} onClick={() => onClearBay(occ.idx)} style={S.unassignBtn} title="Remove from bay">
          Unassign
        </button>
      </div>
      {moved && (
        <button disabled={busy} onClick={() => onReset(occ.idx)} style={S.resetBtn}>
          Reset to programmed bay {occ.programmedBay}
        </button>
      )}
      <div style={S.streetsHead}>Delivers to · {occ.streets.length} street{occ.streets.length === 1 ? "" : "s"}</div>
      {occ.streets.length > 0 ? (
        <div style={S.streets}>
          {occ.streets.map((st, i) => (
            <span key={i} style={S.streetChip}>{st}</span>
          ))}
        </div>
      ) : (
        <p style={S.emptySmall}>No streets recorded on this round.</p>
      )}
    </div>
  );
}

function FilterGroup({ label, value, onChange, options }) {
  return (
    <div style={S.filterGroup}>
      <span style={S.filterLabel}>{label}</span>
      <div style={S.segmented}>
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              onClick={() => onChange(o.v)}
              style={{
                ...S.segBtn,
                ...(active ? S.segBtnActive : null),
                ...(active && o.color ? { background: o.color.fill, color: o.color.text, borderColor: o.color.stroke } : null),
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ n, label, color }) {
  return (
    <div style={S.tallyItem}>
      <span style={{ ...S.tallyCount, color }}>{n}</span>
      <span style={S.tallyLabel}>{label}</span>
    </div>
  );
}

function Swatch({ fill, stroke, label }) {
  return (
    <div style={S.swatchRow}>
      <span style={{ ...S.swatch, background: fill, borderColor: stroke }} />
      <span style={S.swatchLabel}>{label}</span>
    </div>
  );
}

const DRAG_CSS = `
.hp-draggable { cursor: grab; user-select: none; -webkit-user-select: none; }
.hp-draggable:active { cursor: grabbing; }
`;

const S = {
  page: {
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: PALETTE.ink, background: PALETTE.canvas, padding: 24, minHeight: "100%",
  },
  header: {
    display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "space-between",
    alignItems: "flex-end", borderBottom: `1px solid ${PALETTE.hairline}`, paddingBottom: 16, marginBottom: 14,
  },
  title: { fontSize: 22, fontWeight: 500, margin: 0, letterSpacing: "-0.01em" },
  subtitle: { fontSize: 13, color: PALETTE.inkMuted, margin: "4px 0 0" },
  headRight: { display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" },
  tally: { display: "flex", gap: 18 },
  tallyItem: { display: "flex", flexDirection: "column", alignItems: "flex-end" },
  tallyCount: { fontSize: 21, fontWeight: 500, lineHeight: 1.1 },
  tallyLabel: { fontSize: 11.5, color: PALETTE.inkMuted },
  saveBox: { display: "flex", alignItems: "center", gap: 10 },
  saveMsg: { fontSize: 12.5, fontWeight: 500, minWidth: 54, textAlign: "right" },
  refresh: {
    padding: "8px 14px", fontSize: 13, fontFamily: "inherit", color: PALETTE.ink,
    background: "#fff", border: `1px solid ${PALETTE.hairline}`, borderRadius: 8, cursor: "pointer",
  },
  internalBanner: {
    fontSize: 12.5, color: "#3C3489", background: "#EEEDFE", border: "1px solid #C9C6F3",
    borderRadius: 8, padding: "8px 12px", marginBottom: 16,
  },
  filters: { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", marginBottom: 18 },
  filterGroup: { display: "flex", alignItems: "center", gap: 8 },
  filterLabel: { fontSize: 12, color: PALETTE.inkMuted, fontWeight: 500 },
  segmented: { display: "inline-flex", border: `1px solid ${PALETTE.hairline}`, borderRadius: 8, overflow: "hidden" },
  segBtn: {
    padding: "5px 12px", fontSize: 12.5, fontFamily: "inherit", color: PALETTE.inkMuted,
    background: "#fff", border: "none", borderRight: `1px solid ${PALETTE.hairline}`, cursor: "pointer",
  },
  segBtnActive: { background: PALETTE.ink, color: "#fff", fontWeight: 500 },
  clearFilter: {
    padding: "5px 10px", fontSize: 12, fontFamily: "inherit", color: PALETTE.inkMuted,
    background: "transparent", border: "none", textDecoration: "underline", cursor: "pointer",
  },
  error: { background: "#FCEBEB", border: "1px solid #E24B4A", color: "#A32D2D", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
  linkBtn: { background: "none", border: "none", color: "#A32D2D", textDecoration: "underline", cursor: "pointer", fontSize: 13, padding: 0 },
  loading: { padding: 60, textAlign: "center", color: PALETTE.inkMuted, fontSize: 14 },
  body: { display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" },
  mapCol: { flex: "1 1 460px", minWidth: 340 },
  stageViewport: {
    position: "relative", width: "100%", maxWidth: 780, aspectRatio: `${DEPOT_LAYOUT.vbW} / ${DEPOT_LAYOUT.vbH}`,
    containerType: "inline-size", overflow: "hidden", borderRadius: 6,
  },
  stage: { position: "absolute", inset: 0, width: "100%", height: "100%" },
  zoomOut: {
    position: "absolute", top: 8, right: 8, zIndex: 5,
    display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px",
    fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", color: "#fff",
    background: "rgba(44,44,42,0.9)", border: "none", borderRadius: 8, cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
  },
  svg: { position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" },
  overlay: { position: "absolute", inset: 0, pointerEvents: "none" },
  bay: {
    position: "absolute", boxSizing: "border-box", borderStyle: "solid", borderRadius: 3,
    pointerEvents: "auto", cursor: "pointer", overflow: "hidden", padding: 0,
    display: "flex", flexDirection: "column", justifyContent: "flex-end",
  },
  bayNum: {
    position: "absolute", top: 1, left: 0, right: 0, textAlign: "center",
    fontSize: "clamp(6px, 1.25cqw, 10px)", fontWeight: 600, color: PALETTE.inkMuted,
    textShadow: "0 0 2px #fff, 0 0 2px #fff", pointerEvents: "none", lineHeight: 1,
  },
  bayRow: { display: "flex", width: "100%", height: "100%", gap: 0, alignItems: "stretch" },
  chip2: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
    boxSizing: "border-box", borderStyle: "solid", borderWidth: 0.5, padding: "0 1px 1px",
    overflow: "hidden", lineHeight: 1.0, textAlign: "center",
  },
  chipLogin: {
    fontSize: "clamp(6px, 1.3cqw, 10px)", fontWeight: 600, maxWidth: "100%",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  chipProg: {
    fontSize: "clamp(5px, 1.02cqw, 8px)", fontWeight: 500, opacity: 0.7, maxWidth: "100%",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  chipProgMoved: { opacity: 1, fontWeight: 700, textDecoration: "underline" },
  chipAvatar: {
    width: "clamp(11px, 3.1cqw, 22px)", height: "clamp(11px, 3.1cqw, 22px)", borderRadius: "50%",
    objectFit: "cover", marginBottom: 1, border: "1px solid rgba(0,0,0,0.18)", flex: "0 0 auto",
  },
  hint: { fontSize: 12, color: PALETTE.inkMuted, margin: "10px 0 0", maxWidth: 780 },
  panel: { flex: "0 1 300px", minWidth: 260, display: "flex", flexDirection: "column", gap: 16 },
  tray: {
    border: `1.5px dashed ${PALETTE.hairline}`, borderRadius: 12, padding: 12, background: "#FCFBF8",
    transition: "border-color .12s, background .12s",
  },
  trayActive: { borderColor: PALETTE.accent, background: "rgba(29,158,117,0.06)" },
  detail: { border: `1px solid ${PALETTE.hairline}`, borderRadius: 12, padding: 16, background: "#fff" },
  panelKicker: { fontSize: 12, color: PALETTE.inkMuted },
  panelBay: { fontSize: 32, fontWeight: 500, lineHeight: 1.1, margin: "2px 0 12px" },
  empty: { fontSize: 13, color: PALETTE.inkMuted, lineHeight: 1.6, margin: "8px 0 0" },
  emptySmall: { fontSize: 12, color: PALETTE.inkMuted, margin: "4px 0 0", lineHeight: 1.5 },
  card: { border: `1px solid ${PALETTE.hairline}`, borderRadius: 10, padding: 12, marginBottom: 10 },
  profileHead: { display: "flex", gap: 12, alignItems: "center" },
  profileTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  bayTag: {
    marginTop: 4, fontSize: 12.5, fontWeight: 600, color: PALETTE.accent,
    fontFamily: "ui-monospace, Menlo, monospace", wordBreak: "break-all", lineHeight: 1.25,
  },
  photoWrap: {
    position: "relative", flex: "0 0 auto", width: 64, height: 64, borderRadius: "50%",
    overflow: "hidden", cursor: "pointer", background: "#EEEDF2", border: `2px solid ${PALETTE.hairline}`,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  photoImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  photoInitials: { fontSize: 20, fontWeight: 700, color: PALETTE.inkMuted, letterSpacing: "0.02em" },
  photoEdit: {
    position: "absolute", left: 0, right: 0, bottom: 0, textAlign: "center",
    fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
    color: "#fff", background: "rgba(0,0,0,0.42)", padding: "2px 0",
  },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  cardLogin: { fontSize: 16, fontWeight: 600, letterSpacing: "0.01em" },
  waveBadge: { fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, border: "1px solid" },
  progBlock: { margin: "10px 0 0", padding: "8px 10px", background: "#F7F6F1", borderRadius: 8, border: `1px solid ${PALETTE.hairline}` },
  progRow: { display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, padding: "1px 0" },
  progLabel: { color: PALETTE.inkMuted },
  progVal: { fontWeight: 600, textAlign: "right", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11.5 },
  progMovedNote: { color: PALETTE.wave[2].text, fontFamily: "inherit", fontWeight: 600 },
  progAsProg: { color: PALETTE.inkMuted, fontFamily: "inherit", fontWeight: 400 },
  cardMeta: { display: "flex", flexWrap: "wrap", gap: 6, margin: "8px 0 0" },
  metaTag: { fontSize: 11, color: PALETTE.inkMuted, background: "#F3F2EC", border: `1px solid ${PALETTE.hairline}`, borderRadius: 6, padding: "1px 6px" },
  warn: { fontSize: 11.5, color: "#A32D2D", marginTop: 8 },
  multiNote: { fontSize: 11.5, color: PALETTE.zone.racking.text, background: PALETTE.zone.racking.fill, border: `1px solid ${PALETTE.zone.racking.stroke}`, borderRadius: 6, padding: "5px 8px", marginTop: 8 },
  waveRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 10, flexWrap: "wrap" },
  waveRowLabel: { fontSize: 11.5, color: PALETTE.inkMuted, marginRight: 2 },
  waveBtn: {
    padding: "3px 10px", fontSize: 12, fontFamily: "inherit", color: PALETTE.ink,
    background: "#fff", border: `1px solid ${PALETTE.hairline}`, borderRadius: 6, cursor: "pointer",
  },
  waveBtnActive: { background: PALETTE.ink, color: "#fff", borderColor: PALETTE.ink, fontWeight: 500 },
  unassignBtn: {
    marginLeft: "auto", padding: "3px 10px", fontSize: 12, fontFamily: "inherit", color: PALETTE.inkMuted,
    background: "#fff", border: `1px solid ${PALETTE.hairline}`, borderRadius: 6, cursor: "pointer",
  },
  resetBtn: {
    marginTop: 8, padding: "4px 10px", fontSize: 12, fontFamily: "inherit", color: PALETTE.accent,
    background: "#fff", border: `1px solid ${PALETTE.accent}`, borderRadius: 6, cursor: "pointer",
  },
  streetsHead: { fontSize: 12, color: PALETTE.inkMuted, margin: "12px 0 6px", fontWeight: 500 },
  streets: { display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 180, overflowY: "auto" },
  streetChip: { fontSize: 11.5, color: PALETTE.ink, background: "#F7F6F1", border: `1px solid ${PALETTE.hairline}`, borderRadius: 6, padding: "2px 7px" },
  attention: { border: `1px solid ${PALETTE.hairline}`, borderRadius: 12, padding: 14 },
  attnRow: { display: "flex", gap: 8, fontSize: 12.5, padding: "2px 0" },
  attnBay: { color: PALETTE.inkMuted, minWidth: 52 },
  chips: { display: "flex", flexWrap: "wrap", gap: 5 },
  chip: { fontSize: 11.5, color: PALETTE.inkMuted, background: "#F3F2EC", border: `1px solid ${PALETTE.hairline}`, borderRadius: 6, padding: "3px 8px" },
  clear: {
    marginTop: 4, width: "100%", padding: "8px 12px", fontSize: 13, fontFamily: "inherit",
    color: PALETTE.ink, background: "transparent", border: `1px solid ${PALETTE.hairline}`, borderRadius: 8, cursor: "pointer",
  },
  legend: { border: `1px solid ${PALETTE.hairline}`, borderRadius: 12, padding: 14 },
  legendHeading: { fontSize: 12, color: PALETTE.inkMuted, marginBottom: 8, fontWeight: 500 },
  swatchRow: { display: "flex", alignItems: "center", gap: 8, padding: "3px 0" },
  swatch: { width: 13, height: 13, borderRadius: 3, border: "1px solid", flexShrink: 0 },
  swatchLabel: { fontSize: 12.5 },
  zoneLabel: { fontSize: 14, fontWeight: 500, textAnchor: "middle", dominantBaseline: "central" },
  zoneLabelSm: { fontSize: 12, textAnchor: "middle", dominantBaseline: "central" },
  gateLabel: { fontSize: 14, fontWeight: 500, textAnchor: "middle", dominantBaseline: "central" },
  menuBackdrop: { position: "fixed", inset: 0, zIndex: 40 },
  menu: {
    position: "fixed", zIndex: 41, width: 230, background: "#fff",
    border: `1px solid ${PALETTE.hairline}`, borderRadius: 10, padding: 6,
    boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
  },
  menuTitle: { fontSize: 11.5, color: PALETTE.inkMuted, fontWeight: 600, padding: "6px 8px 8px" },
  menuBtn: {
    display: "block", width: "100%", textAlign: "left", padding: "7px 9px", marginBottom: 3,
    fontFamily: "inherit", background: "#fff", border: `1px solid ${PALETTE.hairline}`,
    borderRadius: 7, cursor: "pointer",
  },
  menuShare: { borderColor: PALETTE.accent, background: "rgba(29,158,117,0.06)" },
  menuBtnMain: { display: "block", fontSize: 13, fontWeight: 600, color: PALETTE.ink },
  menuBtnSub: { display: "block", fontSize: 11, color: PALETTE.inkMuted, marginTop: 1 },
  menuCancel: {
    display: "block", width: "100%", padding: "6px 9px", marginTop: 2, fontFamily: "inherit",
    fontSize: 12.5, color: PALETTE.inkMuted, background: "transparent", border: "none", cursor: "pointer",
  },
};
